import React, { useEffect, useState } from 'react';
import { skillService } from '../../services/api';
import CategorySubcategorySelect from '../common/CategorySubcategorySelect';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const SkillManagement = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newSkill, setNewSkill] = useState({ categoryId: '', subcategoryId: '', note: '' });

  const loadSkills = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await skillService.list();
      setSkills(res.data);
    } catch (e) {
      setError('Errore nel caricamento abilità');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSkills(); }, []);

  const handleAdd = async () => {
    if (!newSkill.categoryId) {
      setError('Seleziona una categoria');
      return;
    }
    const duplicate = skills.some(s =>
      String(s.category) === String(newSkill.categoryId) &&
      String(s.subcategory || '') === String(newSkill.subcategoryId || '')
    );
    if (duplicate) {
      setError('Hai già questa abilità');
      return;
    }
    try {
      await skillService.create({
        category: Number(newSkill.categoryId),
        subcategory: newSkill.subcategoryId ? Number(newSkill.subcategoryId) : null,
        note: newSkill.note || '',
      });
      setNewSkill({ categoryId: '', subcategoryId: '', note: '' });
      setSuccess('Abilità inserita');
      loadSkills();
    } catch (e) {
      setError('Errore nell\'inserimento abilità');
    }
  };

  const [editDialog, setEditDialog] = useState({ open: false, skill: null, note: '' });
  const openEditDialog = (skill) => setEditDialog({ open: true, skill, note: skill.note || '' });
  const closeEditDialog = () => setEditDialog({ open: false, skill: null, note: '' });

  const confirmEdit = async () => {
    try {
      await skillService.update(editDialog.skill.id, { note: editDialog.note });
      setSuccess('Abilità aggiornata');
      closeEditDialog();
      loadSkills();
    } catch (e) {
      setError('Errore nell\'aggiornamento abilità');
    }
  };

  const handleDelete = async (skill) => {
    if (!window.confirm('Eliminare questa abilità?')) return;
    const previousSkills = skills;
    setSkills((prev) => prev.filter((s) => s.id !== skill.id));
    try {
      await skillService.delete(skill.id);
      setSuccess('Abilità eliminata');
      await loadSkills();
    } catch (e) {
      setSkills(previousSkills);
      setError('Errore nell\'eliminazione abilità');
    }
  };

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 4, md: 8 }, width: '100%', maxWidth: 1600, mx: 'auto', minWidth: 0, overflow: 'hidden' }}>
                <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'center', position: 'relative', mb: 3 }} >
          <Button  component={Link} to="/dashboard" startIcon={<ArrowBackIcon />} variant="outlined" color="primary"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' }, mb: { xs: 1, sm: 0 }, textTransform: 'none', }} >
            Dashboard
          </Button>

          <Typography component="h1" variant="h4" gutterBottom
            sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%', }} >
            Gestione Abilità
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Sezione inserimento */}
        <Box
          sx={{
            mb: 3,
            display: 'grid',
            gap: 2,
            width: '100%',
            minWidth: 0,
            gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
            alignItems: 'start',
          }}
        >
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
            <Button fullWidth variant="contained" color="success" onClick={handleAdd}>
              Inserisci nuova abilità
            </Button>
          </Box>
          <Box sx={{ minWidth: 0, display: 'grid', gap: 2 }}>
            <CategorySubcategorySelect
              value={{
                categoryId: newSkill.categoryId,
                subcategoryId: newSkill.subcategoryId
              }}
              onChange={(v) => setNewSkill({ ...newSkill, ...v })}
            />
            <TextField
              label="Note"
              value={newSkill.note}
              onChange={(e) => setNewSkill({ ...newSkill, note: e.target.value })}
              fullWidth
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : !isMobile ? (
          <Box sx={{ overflowX: 'auto' }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Sub Categoria</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skills.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.category_label}</TableCell>
                      <TableCell>{s.subcategory_label}</TableCell>
                      <TableCell>{s.note}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => openEditDialog(s)}
                          >
                            Modifica
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleDelete(s)}
                          >
                            Elimina
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Dialog modifica */}
            <Dialog open={editDialog.open} onClose={closeEditDialog}>
              <DialogTitle>Modifica nota abilità</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Nota"
                  fullWidth
                  value={editDialog.note}
                  onChange={(e) => setEditDialog({ ...editDialog, note: e.target.value })}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closeEditDialog}>Annulla</Button>
                <Button variant="contained" onClick={confirmEdit}>Salva</Button>
              </DialogActions>
            </Dialog>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {skills.length > 0 ? (
              skills.map((s) => (
                <Paper key={s.id} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {s.category_label || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {s.subcategory_label || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {s.note || '—'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => openEditDialog(s)}
                    >
                      Modifica
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => handleDelete(s)}
                    >
                      Elimina
                    </Button>
                  </Box>
                </Paper>
              ))
            ) : (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body2" align="center">Nessuna abilità inserita.</Typography>
              </Paper>
            )}

            <Dialog open={editDialog.open} onClose={closeEditDialog}>
              <DialogTitle>Modifica nota abilità</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Nota"
                  fullWidth
                  value={editDialog.note}
                  onChange={(e) => setEditDialog({ ...editDialog, note: e.target.value })}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={closeEditDialog}>Annulla</Button>
                <Button variant="contained" onClick={confirmEdit}>Salva</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SkillManagement;
